/**
 * Tolerant parser for `Opportunity.Next_Step_History__c` — a long-text-area
 * field where SFDC users hand-write dated log entries, one per "save".
 * Observed format (docs/middleman-opportunity-personas-and-activities-ask.md,
 * an actual prod sample):
 *
 *   8/6/2026, 1:07 PM - 08/06 AM U:Intro w/ Steve Trout,new Head of Sales...
 *
 * i.e. a leading `M/D/YYYY, h:mm AM/PM -` timestamp, then free text. There is
 * no format guarantee beyond that observed pattern (it's a human-typed field,
 * not a structured one) — Bucket B command-UX plan Decision 17. This parser
 * is intentionally tolerant:
 *
 *   - A line that matches the timestamp prefix starts a NEW entry.
 *   - A line that doesn't match attaches to the PREVIOUS entry as a
 *     continuation (SFDC's UI soft-wraps long lines; the raw field also
 *     legitimately contains multi-line notes under one timestamp).
 *   - If NOTHING in the whole string matches, the fallback is the "No
 *     Hardcoded Fallbacks" honesty valve: render the raw text as ONE
 *     undated entry rather than guessing a structure that isn't there.
 *
 * Never throws — this feeds directly into a render path; a parse failure
 * must degrade to raw text, not blow up the Opportunities tab.
 */

export interface NextStepHistoryEntry {
  /** The matched timestamp prefix verbatim (e.g. "8/6/2026, 1:07 PM"), or
   * `null` when the whole raw string didn't match the pattern anywhere. */
  when: string | null;
  text: string;
}

// `M/D/YYYY, h:mm AM/PM -` — 1-2 digit month/day, 4-digit year, 1-2 digit
// hour, 2-digit minute, AM/PM (case-insensitive), then a literal dash.
// Captures the timestamp (group 1) and the rest of the line (group 2).
const ENTRY_HEADER_RE = /^(\d{1,2}\/\d{1,2}\/\d{4},\s*\d{1,2}:\d{2}\s*[AP]M)\s*-\s*(.*)$/i;

/**
 * Parses `raw` into dated entries, newest-first AS GIVEN in the raw text
 * (SFDC's long-text-area field itself already orders newest-first — this
 * function does not re-sort; it only splits/groups).
 */
export function parseNextStepHistory(raw: string | null | undefined): NextStepHistoryEntry[] {
  if (!raw || !raw.trim()) return [];

  const lines = raw.split('\n');
  const entries: NextStepHistoryEntry[] = [];

  for (const line of lines) {
    const match = ENTRY_HEADER_RE.exec(line);
    if (match) {
      entries.push({ when: match[1], text: match[2] });
      continue;
    }
    if (line.trim() === '') continue;
    if (entries.length > 0) {
      // Continuation of the previous dated entry.
      const prev = entries[entries.length - 1];
      prev.text = prev.text ? `${prev.text}\n${line}` : line;
    } else {
      // Nothing dated has matched yet. Defer — if the rest of the string
      // also never matches, this becomes the single raw-fallback entry
      // below; if a later line DOES match, this line is genuinely an
      // undated leading continuation, so fold it into that first real entry.
      entries.push({ when: null, text: line });
    }
  }

  // Raw fallback: no line ever matched the timestamp pattern at all — one
  // undated entry carrying the ORIGINAL raw string (not the trimmed/
  // reassembled line-join), so nothing is silently altered.
  if (entries.length > 0 && entries.every(e => e.when === null)) {
    return [{ when: null, text: raw }];
  }

  // Drop a leading undated placeholder if a later line supplied the real
  // date (fold its text into that first dated entry instead of showing a
  // spurious separate "undated" block ahead of it).
  if (entries.length > 1 && entries[0].when === null) {
    const [leading, ...rest] = entries;
    if (rest[0]) rest[0].text = `${leading.text}\n${rest[0].text}`;
    return rest;
  }

  return entries;
}
